import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { imgSrc } from "@/lib/api";

export default function Lightbox({ images, index, onClose, onIndex }) {
  const open = index !== null && index >= 0;

  const prev = useCallback(() => onIndex((index - 1 + images.length) % images.length), [index, images.length, onIndex]);
  const next = useCallback(() => onIndex((index + 1) % images.length), [index, images.length, onIndex]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, prev, next]);

  // swipe
  let startX = 0;
  const onTouchStart = (e) => { startX = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - startX;
    if (dx > 60) prev();
    if (dx < -60) next();
  };

  const img = open ? images[index] : null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={onClose}
          data-testid="lightbox"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <button className="absolute top-5 right-5 text-white/80 hover:text-white p-2 z-10" onClick={onClose} data-testid="lightbox-close" aria-label="Close">
            <X size={30} />
          </button>
          <button className="absolute left-3 sm:left-8 text-white/70 hover:text-[#02AACF] p-2 z-10" onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="lightbox-prev" aria-label="Previous">
            <ChevronLeft size={40} />
          </button>
          <button className="absolute right-3 sm:right-8 text-white/70 hover:text-[#02AACF] p-2 z-10" onClick={(e) => { e.stopPropagation(); next(); }} data-testid="lightbox-next" aria-label="Next">
            <ChevronRight size={40} />
          </button>

          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35 }}
            className="max-w-[92vw] max-h-[86vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={imgSrc(img)} alt={img?.alt_text || img?.title} className="max-w-[92vw] max-h-[78vh] object-contain rounded-lg" />
            <div className="mt-4 text-center">
              <p className="text-white font-display font-semibold">{img?.title}</p>
              <p className="font-mono2 text-xs tracking-[0.2em] text-[#02AACF] uppercase mt-1">{img?.category} · {index + 1}/{images.length}</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
